import { SourceChannelAssignment } from 'api/markerTypes';
import {
  ChannelNormalizationValueModel,
  SlideChannelNormalizationResultSummary,
} from 'api/slideMultiplexChannelNormalizations';
import { StainType } from 'interfaces/stainType';
import { compact, every, filter, find, flatMap, includes, map, reduce, some, sortBy, toLower, uniq } from 'lodash';

export const MISSING_MARKER_NAME_LABEL = 'Missing marker name';

export interface NormalizationRange {
  min: number;
  max: number;
}

export type MarkerCurrentRangeStatus = 'none' | 'mixed' | 'consistent';

export interface MarkerCurrentRangeDetails {
  status: MarkerCurrentRangeStatus;
  range?: NormalizationRange;
}

export interface MissingMarkerNameChannel {
  channelId: string;
  sourceNames: string[];
}

export interface ChannelMarkerNameStateRow {
  channelId: string;
  sourceNames: string[];
  assignedMarkerTypes: string[];
  isDna: boolean;
  isMissingMarkerName: boolean;
}

const DNA_MARKER_IDS = ['dapi', 'hoechst', 'syto'];

const getFiniteNumber = (value: unknown): number | undefined => {
  if (typeof value === 'number' && Number.isFinite(value)) {
    return value;
  }

  return undefined;
};

const getChannelCurrentRange = (channel: ChannelNormalizationValueModel): NormalizationRange | undefined => {
  const minimumValue = getFiniteNumber(channel.channelNormalizationResults?.params?.min_c);
  const maximumValue = getFiniteNumber(channel.channelNormalizationResults?.params?.max_c);

  if (minimumValue === undefined || maximumValue === undefined) {
    return undefined;
  }

  return { min: minimumValue, max: maximumValue };
};

export const isDnaMarker = ({
  markerId,
  sourceNames = [],
  stainTypeOptions,
}: {
  markerId?: string | null;
  sourceNames?: string[];
  stainTypeOptions: StainType[];
}) => {
  const stainType = markerId ? find(stainTypeOptions, { id: markerId }) : undefined;
  if (stainType?.dna) {
    return true;
  }

  const signals = compact([markerId, stainType?.displayName, ...sourceNames]);
  return some(signals, (value) => {
    const normalizedValue = toLower(value);
    return includes(DNA_MARKER_IDS, normalizedValue) || includes(normalizedValue, 'dna');
  });
};

const getDnaMarkerIdFromSourceNames = (sourceNames: string[], stainTypeOptions: StainType[]) =>
  find(stainTypeOptions, (stainType) => {
    if (!stainType.dna) {
      return false;
    }

    const stainNames = compact([stainType.id, stainType.displayName]);
    return some(sourceNames, (sourceName) =>
      some(stainNames, (stainName) => toLower(stainName) === toLower(sourceName))
    );
  })?.id;

export const getConfigurableMarkerIds = (sourceChannels: SourceChannelAssignment[], stainTypeOptions: StainType[]) =>
  sortBy(
    uniq(
      compact(
        flatMap(sourceChannels, (channel) =>
          channel.assignedMarkerTypes.length > 0
            ? channel.assignedMarkerTypes
            : [getDnaMarkerIdFromSourceNames(channel.sourceNames, stainTypeOptions)]
        )
      )
    )
  );

export const getMissingMarkerNameChannels = (
  sourceChannels: SourceChannelAssignment[],
  stainTypeOptions: StainType[]
): MissingMarkerNameChannel[] =>
  map(
    filter(
      sourceChannels,
      (channel) =>
        channel.assignedMarkerTypes.length === 0 && !isDnaMarker({ sourceNames: channel.sourceNames, stainTypeOptions })
    ),
    (channel) => ({
      channelId: channel.channelId,
      sourceNames: channel.sourceNames,
    })
  );

export const getChannelMarkerNameStateRows = (
  sourceChannels: SourceChannelAssignment[],
  stainTypeOptions: StainType[]
): ChannelMarkerNameStateRow[] =>
  map(sourceChannels, (channel) => {
    const isDna = isDnaMarker({
      markerId: find(channel.assignedMarkerTypes, (markerId) => isDnaMarker({ markerId, stainTypeOptions })),
      sourceNames: channel.sourceNames,
      stainTypeOptions,
    });

    return {
      channelId: channel.channelId,
      sourceNames: channel.sourceNames,
      assignedMarkerTypes: channel.assignedMarkerTypes,
      isDna,
      isMissingMarkerName: !isDna && channel.assignedMarkerTypes.length === 0,
    };
  });

export const getCurrentMarkerRangeDetails = (
  normalizationSummaries: SlideChannelNormalizationResultSummary[],
  markerId: string,
  requiredSlideIds: string[] = map(normalizationSummaries, 'slideId')
): MarkerCurrentRangeDetails => {
  const currentRangesBySlide = map(requiredSlideIds, (slideId) => {
    const normalizationSummary = find(normalizationSummaries, { slideId });
    if (!normalizationSummary) {
      return undefined;
    }

    return compact(
      map(normalizationSummary.normalizationsSummary, (channel) =>
        channel.markerType === markerId ? getChannelCurrentRange(channel) : undefined
      )
    )[0];
  });
  const currentRanges = compact(currentRangesBySlide);

  if (currentRanges.length === 0) {
    return { status: 'none' };
  }

  if (currentRanges.length !== requiredSlideIds.length) {
    return { status: 'mixed' };
  }

  const [firstRange] = currentRanges;
  if (every(currentRanges, (range) => range.min === firstRange.min && range.max === firstRange.max)) {
    return { status: 'consistent', range: firstRange };
  }

  return { status: 'mixed' };
};

export const hasCompleteMarkerNormalization = (
  normalizationSummaries: SlideChannelNormalizationResultSummary[],
  markerId: string,
  requiredSlideIds: string[] = map(normalizationSummaries, 'slideId')
) =>
  requiredSlideIds.length > 0 &&
  every(requiredSlideIds, (slideId) => {
    const normalizationSummary = find(normalizationSummaries, { slideId });
    return Boolean(
      normalizationSummary &&
        some(
          normalizationSummary.normalizationsSummary,
          (channel) => channel.markerType === markerId && Boolean(getChannelCurrentRange(channel))
        )
    );
  });

export const getDefaultRangesByMarkerId = (
  normalizationSummaries: SlideChannelNormalizationResultSummary[],
  markerIds: string[],
  requiredSlideIdsByMarker: Record<string, string[]> = {}
) =>
  reduce(
    markerIds,
    (rangesByMarkerId, markerId) => {
      const currentRangeDetails = getCurrentMarkerRangeDetails(
        normalizationSummaries,
        markerId,
        requiredSlideIdsByMarker[markerId]
      );
      if (currentRangeDetails.range) {
        rangesByMarkerId[markerId] = currentRangeDetails.range;
      }
      return rangesByMarkerId;
    },
    {} as Record<string, NormalizationRange>
  );
